import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { 
  Users, 
  Key, 
  Plus, 
  Trash2, 
  Copy, 
  Loader2,
  Shield,
  Calendar,
  Hash
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminPanel = ({ token, onClose }) => {
  const [activeTab, setActiveTab] = useState("codes");
  const [accessCodes, setAccessCodes] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [newCode, setNewCode] = useState({ maxUses: 1, expiresDays: 30 });

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchAccessCodes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/access-codes`, { headers: authHeaders });
      setAccessCodes(response.data);
    } catch (error) {
      toast.error("Failed to fetch access codes");
    }
  }, [token]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, { headers: authHeaders });
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to fetch users");
    }
  }, [token]);

  useEffect(() => {
    fetchAccessCodes();
    fetchUsers();
  }, [fetchAccessCodes, fetchUsers]);

  const createAccessCode = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/admin/access-codes`, {
        max_uses: newCode.maxUses,
        expires_days: newCode.expiresDays
      }, { headers: authHeaders });
      
      toast.success(`Access code created: ${response.data.code}`);
      fetchAccessCodes();
      setNewCode({ maxUses: 1, expiresDays: 30 });
    } catch (error) {
      toast.error("Failed to create access code");
    } finally {
      setIsLoading(false);
    }
  };

  const deleteAccessCode = async (codeId) => {
    try {
      await axios.delete(`${API}/admin/access-codes/${codeId}`, { headers: authHeaders });
      toast.success("Access code deactivated");
      fetchAccessCodes();
    } catch (error) {
      toast.error("Failed to delete access code");
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Copied: ${code}`);
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 border border-zinc-800 w-full max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-yellow-500" />
            <h2 className="text-lg font-mono font-bold text-white">ADMIN PANEL</h2>
          </div>
          <Button
            variant="ghost"
            onClick={onClose}
            className="text-zinc-500 hover:text-white"
          >
            ✕
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-zinc-800">
          <button
            onClick={() => setActiveTab("codes")}
            className={`flex-1 p-3 text-sm font-mono uppercase tracking-wider transition-colors ${
              activeTab === "codes"
                ? "text-yellow-500 border-b-2 border-yellow-500 bg-zinc-800/50"
                : "text-zinc-500 hover:text-zinc-300"
            }`}
          >
            <Key className="w-4 h-4 inline mr-2" />
            Access Codes
          </button>
          <button
            onClick={() => setActiveTab("users")}
            className={`flex-1 p-3 text-sm font-mono uppercase tracking-wider transition-colors ${
              activeTab === "users"
                ? "text-yellow-500 border-b-2 border-yellow-500 bg-zinc-800/50"
                : "text-zinc-500 hover:text-zinc-300"
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            Users ({users.length})
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {activeTab === "codes" && (
            <div className="space-y-4">
              {/* Create New Code */}
              <div className="bg-zinc-800/50 p-4 border border-zinc-700">
                <h3 className="text-sm font-mono text-zinc-400 mb-3">CREATE NEW ACCESS CODE</h3>
                <div className="flex gap-4 items-end">
                  <div>
                    <Label className="text-xs text-zinc-500">Max Uses</Label>
                    <Input
                      type="number"
                      min="1"
                      value={newCode.maxUses}
                      onChange={(e) => setNewCode({ ...newCode, maxUses: parseInt(e.target.value) })}
                      className="w-24 bg-zinc-900 border-zinc-700 rounded-none"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-500">Expires (days)</Label>
                    <Input
                      type="number"
                      min="1"
                      value={newCode.expiresDays}
                      onChange={(e) => setNewCode({ ...newCode, expiresDays: parseInt(e.target.value) })}
                      className="w-24 bg-zinc-900 border-zinc-700 rounded-none"
                    />
                  </div>
                  <Button
                    onClick={createAccessCode}
                    disabled={isLoading}
                    className="bg-yellow-500 hover:bg-yellow-600 text-black font-mono rounded-none"
                  >
                    {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                    Generate Code
                  </Button>
                </div>
              </div>

              {/* Access Codes Table */}
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800">
                    <TableHead className="text-zinc-500 font-mono">CODE</TableHead>
                    <TableHead className="text-zinc-500 font-mono">USES</TableHead>
                    <TableHead className="text-zinc-500 font-mono">EXPIRES</TableHead>
                    <TableHead className="text-zinc-500 font-mono">STATUS</TableHead>
                    <TableHead className="text-zinc-500 font-mono">ACTIONS</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {accessCodes.map((code) => (
                    <TableRow key={code.id} className="border-zinc-800">
                      <TableCell className="font-mono text-green-400">
                        {code.code}
                        <button onClick={() => copyCode(code.code)} className="ml-2 text-zinc-500 hover:text-white">
                          <Copy className="w-3 h-3" />
                        </button>
                      </TableCell>
                      <TableCell className="font-mono">
                        {code.uses} / {code.max_uses}
                      </TableCell>
                      <TableCell className="font-mono text-zinc-400">
                        {new Date(code.expires_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {code.is_active ? (
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/30 rounded-none">
                            ACTIVE
                          </Badge>
                        ) : (
                          <Badge className="bg-red-500/20 text-red-400 border-red-500/30 rounded-none">
                            INACTIVE
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {code.is_active && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteAccessCode(code.id)}
                            className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {activeTab === "users" && (
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800">
                  <TableHead className="text-zinc-500 font-mono">USERNAME</TableHead>
                  <TableHead className="text-zinc-500 font-mono">ROLE</TableHead>
                  <TableHead className="text-zinc-500 font-mono">SEARCHES</TableHead>
                  <TableHead className="text-zinc-500 font-mono">JOINED</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} className="border-zinc-800">
                    <TableCell className="font-mono text-white">{user.username}</TableCell>
                    <TableCell>
                      {user.is_admin ? (
                        <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 rounded-none">
                          <Shield className="w-3 h-3 mr-1" />
                          ADMIN
                        </Badge>
                      ) : (
                        <Badge className="bg-zinc-500/20 text-zinc-400 border-zinc-500/30 rounded-none">
                          USER
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-mono">
                      <Hash className="w-3 h-3 inline mr-1 text-zinc-500" />
                      {user.searches_count}
                    </TableCell>
                    <TableCell className="font-mono text-zinc-400">
                      {new Date(user.created_at).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
